import { Order, OrderItems, OrderItemStatus } from "~/application/types";
import { ExpensePolicyTypeValue } from "~/application/types";
import {
  SvgAirplane,
  SvgAnalytics,
  SvgBus,
  SvgCar,
  SvgCurrencyExchange,
  SvgExpenseReport,
  SvgGear,
  SvgHotelOffline,
  SvgHotels,
  SvgMetric,
  SvgOrder,
  SvgRequestQuote,
  SvgSearch,
  SvgTicket,
} from "~/components/Icon/icons";
import {
  DateFormats,
  displayDate,
  isDateValid,
  isSameDay,
  maxDate,
  minDate,
} from "~/utils/date.utils";
import * as MaskUtils from "~/utils/mask.utils";
import React from "react";
import { MenuItems } from "~/presentation/core/containers/AppContainer/components/NavMenuMobile/NavMenuMobile";
import { routes } from "~/application/theme/routes";

export function getServiceLabel(service: OrderItems | string): string {
  switch (service) {
    case OrderItems.AIRWAY:
      return "Aéreo";
    case OrderItems.HOTEL:
      return "Hotel";
    case OrderItems.VEHICLE:
      return "Carro";
    case OrderItems.ROAD:
      return "Rodoviário";
    case OrderItems.ADVANCE:
      return "Adiantamento";
    case OrderItems.HOTEL_OFFLINE:
      return "Hotel Offline";
    case OrderItems.EXPENSE_REPORT:
      return "Despesas";
    case OrderItems.EXPENSE:
      return "Despesa";
    default:
      return service;
  }
}

export function getServiceMobileLabel(service: MenuItems): string {
  switch (service) {
    case MenuItems.SEARCH:
      return "Início";
    case MenuItems.EXPENSES:
      return "Despesas";
    case MenuItems.ORDER:
      return "Pedidos";
    case MenuItems.SETTINGS:
      return "Configurações";
    case MenuItems.DASHBOARD:
      return "Dashboard";
    case MenuItems.REPORTING_CENTER:
      return "Central de relatórios";
    case MenuItems.NOTIFICATIONS:
      return "Notificações";
  }
}

export function getServiceLink(service: OrderItems | string): string | undefined {
  switch (service) {
    case OrderItems.AIRWAY:
      return routes.Booking.Airway.root;
    case OrderItems.HOTEL:
      return routes.Booking.Hotel.root;
    case OrderItems.VEHICLE:
      return routes.Booking.Vehicle.root;
    case OrderItems.ROAD:
      return routes.Booking.Road.root;
    case OrderItems.ADVANCE:
      return routes.Expenses.RequestAdvance.root;
    case "expense":
      return routes.Expenses.SubmitExpense.root;
  }
}

export function getServiceIcon(service: OrderItems | string): React.ElementType | undefined {
  switch (service) {
    case OrderItems.AIRWAY:
      return SvgAirplane;
    case OrderItems.HOTEL:
      return SvgHotels;
    case OrderItems.VEHICLE:
      return SvgCar;
    case OrderItems.ROAD:
      return SvgBus;
    case OrderItems.ADVANCE:
      return SvgCurrencyExchange;
    case OrderItems.HOTEL_OFFLINE:
      return SvgHotelOffline;
    case OrderItems.EXPENSE:
      return SvgRequestQuote;
    case OrderItems.EXPENSE_REPORT:
      return SvgExpenseReport;
  }
}

export function getServiceMenuIcon(service: MenuItems): React.ElementType | undefined {
  switch (service) {
    case MenuItems.SEARCH:
      return SvgSearch;
    case MenuItems.EXPENSES:
      return SvgOrder;
    case MenuItems.ORDER:
      return SvgTicket;
    case MenuItems.SETTINGS:
      return SvgGear;
    case MenuItems.DASHBOARD:
      return SvgAnalytics;
    case MenuItems.REPORTING_CENTER:
      return SvgMetric;
    case MenuItems.NOTIFICATIONS:
      return SvgGear;
  }
}

export function getUnsatisfiedPolicyMessage(typeValue: ExpensePolicyTypeValue, value: number) {
  switch (typeValue) {
    case ExpensePolicyTypeValue.AMOUNT_MAXIMUM:
      return `A opção selecionada está acima da quantidade máxima máximo definido de ${value}.`;
    case ExpensePolicyTypeValue.TOUCHABLE_LESS:
      return `A opção selecionada está acima do teto máximo definido de ${MaskUtils.asCurrency(
        value,
      )}.`;
    case ExpensePolicyTypeValue.UNTOUCHABLE:
      return `A opção selecionada não se enquadra no valor definido de ${MaskUtils.asCurrency(
        value,
      )}.`;
    default:
      return "";
  }
}

export function getTotalOrderItems(order?: Order): number {
  const totalRoadItems = order?.items.road?.travels.length || 0;
  const totalHotelItems = order?.items.hotel?.rooms.length || 0;
  const totalAirwayItems = order?.items.airway?.flights.length || 0;
  const totalVehicleItems = order?.items.vehicle?.vehicles.length || 0;

  return totalAirwayItems + totalVehicleItems + totalHotelItems + totalRoadItems;
}

export function getTotalCancellationTax(order?: Order): number {
  const roadCancellationTax = calculateRoadCancellationTax(order);

  return roadCancellationTax;
}

export function getOrderItemValue(order: Order, item: OrderItems): number {
  const orderItem = order.items[item as keyof typeof order.items];

  if (!orderItem) return 0;

  if (item === OrderItems.ROAD) {
    const cancellationTax = calculateRoadCancellationTax(order);
    return orderItem.value - cancellationTax;
  }

  return orderItem.value;
}

export function calculateRoadCancellationTax(order?: Order): number {
  return (
    order?.items.road?.travels
      .filter((travel) => travel.status === OrderItemStatus.CANCELED)
      .reduce((total, travel) => total + (travel?.cancellationTax ?? 0), 0) ?? 0
  );
}

function extractIataCode(airportDescription: string): string {
  const match = airportDescription.match(/\(([A-Z]{3})\)\s*$/);

  return match ? match[1] : airportDescription;
}

export function getOrderDateRange(order: Order): { start: Date; end: Date } | null {
  const destinations = order.destinations;
  const hotelRooms = [
    ...(order.items[OrderItems.HOTEL]?.rooms ?? []),
    ...(order.items[OrderItems.HOTEL_OFFLINE]?.rooms ?? []),
  ];
  const vehicles = order.items[OrderItems.VEHICLE]?.vehicles ?? [];

  const dates: Date[] = [
    ...(destinations?.airwayOrders.map((item) => item.departureDate) ?? []),
    ...(destinations?.roadOrders.map((item) => item.departureDate) ?? []),
    ...(destinations?.hotelOrders.map((item) => item.checkinDate) ?? []),
    ...(destinations?.hotelOrders.flatMap((item) =>
      item.checkoutDate ? [item.checkoutDate] : [],
    ) ?? []),
    ...(destinations?.vehicleOrders.map((item) => item.pickupDate) ?? []),
    ...(destinations?.vehicleOrders.flatMap((item) =>
      item.dropoffDate ? [item.dropoffDate] : [],
    ) ?? []),
    ...hotelRooms.flatMap((item) => [item.checkIn, item.checkOut]),
    ...vehicles.flatMap((item) => [item.departureDate, item.arrivalDate]),
  ].filter(isDateValid);

  if (!dates.length) {
    return order.nearestServiceDate && isDateValid(order.nearestServiceDate)
      ? { start: order.nearestServiceDate, end: order.nearestServiceDate }
      : null;
  }

  return { start: minDate(dates), end: maxDate(dates) };
}

export function formatOrderDateRange(
  dateRange?: { start: Date; end: Date } | null,
  format: string = DateFormats.LONG_BRAZILIAN_DATE,
): string | null {
  if (!dateRange) return null;

  const { start, end } = dateRange;
  if (!isDateValid(start) || !isDateValid(end)) return null;

  if (isSameDay(start, end)) {
    return displayDate(start, format);
  }

  return `${displayDate(start, format)} - ${displayDate(end, format)}`;
}

export type OrderServiceSummary = {
  title: string;
  subtitle?: string;
  extraCount: number;
};

export function getOrderServiceSummary(
  order: Order,
  service: OrderItems,
): OrderServiceSummary | null {
  const destinations = order.destinations;

  switch (service) {
    case OrderItems.AIRWAY: {
      const flights = destinations?.airwayOrders ?? [];
      const [first] = flights;
      if (!first) return null;

      return {
        title: `${extractIataCode(first.departureAirport)} → ${extractIataCode(first.arrivalAirport)}`,
        extraCount: flights.length - 1,
      };
    }
    case OrderItems.ROAD: {
      const travels = destinations?.roadOrders ?? [];
      const [first] = travels;
      if (!first) return null;

      return {
        title: `${first.originCity} → ${first.destinationCity}`,
        extraCount: travels.length - 1,
      };
    }
    case OrderItems.HOTEL:
    case OrderItems.HOTEL_OFFLINE: {
      const rooms = destinations?.hotelOrders ?? [];
      const [first] = rooms;
      if (!first) return null;

      return {
        title: first.hotelName,
        subtitle: first.city,
        extraCount: rooms.length - 1,
      };
    }
    case OrderItems.VEHICLE: {
      const vehicles = destinations?.vehicleOrders ?? [];
      const [first] = vehicles;
      if (!first) return null;

      return {
        title: first.category,
        subtitle: first.rentalCompany,
        extraCount: vehicles.length - 1,
      };
    }
    case OrderItems.ADVANCE:
    case OrderItems.EXPENSE_REPORT:
      return { title: getServiceLabel(service), extraCount: 0 };
    default:
      return { title: getServiceLabel(service), extraCount: 0 };
  }
}

export function splitTravelersIntoColumns<T>(items: T[]): T[][] {
  if (!items || items.length === 0) return [];
  if (items.length <= 3) return [items];
  if (items.length === 4) return [items.slice(0, 2), items.slice(2, 4)];

  const columns: T[][] = [];
  for (let i = 0; i < items.length; i += 3) {
    columns.push(items.slice(i, i + 3));
  }
  return columns;
}
